
import React, { useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { usersService } from "@/services";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { User, Briefcase, Building2, Users, ArrowRight, Loader2, Lock } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const roles = [
  {
    value: "resident",
    label: "Resident",
    icon: User,
    description: "Report issues in your neighborhood and track their progress",
    color: "from-blue-500 to-blue-600",
    features: ["Report infrastructure issues", "Track report status", "Upvote & comment", "View community activity"],
    available: true,
    hidden: true // Hide from UI but keep code
  },
  {
    value: "estate_manager",
    label: "Estate Manager",
    icon: Building2,
    description: "Manage and monitor issues within your estate or property",
    color: "from-orange-500 to-orange-600",
    features: ["View estate reports", "Manage internal issues", "Export reports", "Estate analytics"],
    available: true
  },
  {
    value: "contractor",
    label: "Contractor",
    icon: Briefcase,
    description: "",
    color: "from-purple-500 to-purple-600",
    features: [],
    available: false,
    comingSoon: true
  },
];

export default function RoleSetup() {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { user, isLoadingAuth: userLoading, markRoleUpdating, updateUserState } = useAuth();

  const setupRoleMutation = useMutation({
    mutationFn: async (data) => {
      return await usersService.updateMe(data);
    },
    onSuccess: () => {
      // Show different welcome message based on role
      const roleMessages = {
        resident: "Welcome! Start reporting issues in your neighborhood.",
        estate_manager: "Welcome! You can now manage your estate."
      };
      
      toast.success(`🎉 ${roleMessages[selectedRole]}`, {
        duration: 3000,
      });
      
      // Immediately update the user state so Layout sees the new role
      updateUserState({ user_role: selectedRole });
      
      // Navigate immediately - user state is now updated
      // Check for a pending invite redirect (set when user signed up via an invite link)
      const pendingRedirect = localStorage.getItem("pending_post_signup_redirect");
      // Only follow relative paths — never external URLs (open-redirect prevention)
      if (pendingRedirect && pendingRedirect.startsWith("/")) {
        localStorage.removeItem("pending_post_signup_redirect");
        navigate(pendingRedirect);
      } else if (selectedRole === 'estate_manager') {
        navigate(createPageUrl("CreateEstate"));
      } else {
        navigate(createPageUrl("MyReports"));
      }
    },
    onError: (error) => {
      setIsSubmitting(false);
      toast.error("Failed to set up your profile. Please try again.");
      console.error(error);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isSubmitting || setupRoleMutation.isPending) {
      return;
    }
    
    if (!selectedRole) {
      toast.error("Please select your role to continue");
      return;
    }

    setIsSubmitting(true);

    // Mark that we're updating the role to prevent AuthContext from reloading
    if (markRoleUpdating) {
      markRoleUpdating();
    }

    const updateData = {
      user_role: selectedRole,
    };

    setupRoleMutation.mutate(updateData);
  };

  if (userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F0F0F0' }}>
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#1883FF' }} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-blue-50 p-4">
        <Card className="p-8 text-center max-w-md">
          <Users className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4">Welcome to FixMyHood</h2>
          <p className="text-gray-600 mb-6">Please log in to continue</p>
          <Button 
            onClick={() => navigate('/Login')}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            Login to Get Started
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-8 overflow-y-auto">
      <div className="max-w-6xl mx-auto pb-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12 mt-8"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/30">
            <Users className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Welcome to FixMyHood
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Let's set up your profile. Choose your role to get started!
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Role Selection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Select Your Role
            </h2>
            
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 transition-opacity ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
              {roles.filter(role => !role.hidden).map((role, index) => {
                const Icon = role.icon;
                const isSelected = selectedRole === role.value;
                const isDisabled = !role.available;
                
                return (
                  <motion.div
                    key={role.value}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    whileHover={!isDisabled && !isSubmitting ? { y: -8 } : {}}
                    className={(isDisabled || isSubmitting) ? "cursor-not-allowed" : "cursor-pointer"}
                    onClick={() => !isDisabled && !isSubmitting && setSelectedRole(role.value)}
                  >
                    <Card
                      className={`p-6 h-full transition-all duration-300 relative ${
                        isDisabled
                          ? 'opacity-60 bg-slate-50 dark:bg-slate-800/50'
                          : isSelected
                          ? 'ring-4 ring-emerald-500 shadow-2xl bg-white dark:bg-gray-800'
                          : 'hover:shadow-xl bg-white/80 dark:bg-gray-800/80'
                      }`}
                    >
                      {role.comingSoon && (
                        <Badge className="absolute top-4 right-4 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200">
                          <Lock className="w-3 h-3 mr-1" />
                          Coming Soon
                        </Badge>
                      )}
                      
                      <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-br ${role.color} shadow-lg`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      
                      <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
                        {role.label}
                      </h3>
                      
                      {role.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                          {role.description}
                        </p>
                      )}

                      {role.features.length > 0 && (
                        <div className="space-y-2">
                            {role.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                              <div className={`w-1.5 h-1.5 rounded-full ${isDisabled ? 'bg-slate-400' : 'bg-emerald-500'}`} />
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {isSelected && !isDisabled && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-center"
                        >
                          <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                            ✓ Selected
                          </p>
                        </motion.div>
                      )}
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Submit Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex justify-center pt-6"
          >
            <Button
              type="submit"
              size="lg"
              disabled={!selectedRole || setupRoleMutation.isPending || isSubmitting}
              className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-lg px-12 py-6 shadow-xl shadow-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
            >
              {(setupRoleMutation.isPending || isSubmitting) ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Setting up your profile...
                </>
              ) : (
                <>
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </motion.div>
        </form>

        {/* Full-page loading overlay during submission */}
        {(setupRoleMutation.isPending || isSubmitting) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
          >
            <Card className="p-8 max-w-md mx-4 text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4"
              />
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Setting Up Your Profile
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Please wait while we prepare your account...
              </p>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
